import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  TemplateFlag,
  type TemplateFlagValue,
  useEmailTemplates
} from '~/api/queries/email'
import { EditorInput, TextInput } from '~/components/Form'
import { ToggleInput } from '~/components/Form/Input/ToggleInput'
import { EmailTemplateSelector } from '~/components/Scopes/Actions/Components/EmailTemplateSelector'
import { applicantModalOrderNote } from '~/components/Scopes/Applicants/ApplicantModal/ApplicantModal.css'
import { useConfig } from '~/domains/configuration/queries/config'
import { OrderStatusDropdown } from '~/domains/orders/components/OrderStatusDropdown'
import type { TaxonomyItems } from '~/domains/taxonomies/types'
import type { BulkProcessAction } from '../mutations/bulkProcessApplications'
import { bulkEmailHint } from './BulkApplicationModal.css'
import type { BulkProcessFormValues } from './bulkProcessSchema'

type BulkProcessFieldsProps = {
  orderStatuses: TaxonomyItems<'applicant_order_statuses'>
  applicationCount: number
  /** Only set when the selection holds several applications of a same candidate */
  distinctRecipientCount?: number
}

const templateFlagByAction: Record<BulkProcessAction, TemplateFlagValue> = {
  decline: TemplateFlag.ApplicationRefused,
  integrate: TemplateFlag.ApplicationIntegrated,
  integrate_accept: TemplateFlag.ApplicationAccepted
}

export const BulkProcessFields = ({
  orderStatuses,
  applicationCount,
  distinctRecipientCount
}: BulkProcessFieldsProps) => {
  const { watch, register, setValue, formState } =
    useFormContext<BulkProcessFormValues>()
  // Inputs expect the loosely typed control shared by every form of the app
  const { control } = useFormContext()
  const action = watch('action')
  const sendEmail = watch('sendEmail')
  const templateId = watch('templateId')
  const { data: autoEmailEnabled } = useConfig(
    'application.response_auto_enabled'
  )

  // Left off, the automatic response applies: the template of the action is sent to each applicant
  // when it is enabled, and nothing is sent otherwise
  const emailHint =
    !sendEmail && autoEmailEnabled
      ? 'Chaque candidat recevra une réponse automatique correspondant à sa candidature.'
      : null

  // A candidate who applied several times is written to once, whatever the number of applications
  const recipientHint =
    (sendEmail || autoEmailEnabled) && distinctRecipientCount !== undefined
      ? `${distinctRecipientCount} destinataire${distinctRecipientCount > 1 ? 's' : ''} uniques`
      : null

  const { data: templates } = useEmailTemplates()
  const defaultTemplate = templates?.find(
    (template) => template.flag === templateFlagByAction[action]
  )

  useEffect(() => {
    if (!defaultTemplate) {
      return
    }
    setValue('templateId', defaultTemplate.id)
    setValue('emailSubject', defaultTemplate.subject)
    setValue('emailContent', defaultTemplate.content)
  }, [defaultTemplate, setValue])

  return (
    <>
      {action === 'integrate_accept' && (
        <>
          <OrderStatusDropdown
            label="Statut sur la commande"
            name="orderStatus"
            required
            statuses={orderStatuses}
            register={register}
            control={control}
            setValue={setValue}
            watch={watch}
            error={formState.errors.orderStatus}
          />
          <div className={applicantModalOrderNote}>
            <EditorInput
              label=""
              name="note"
              showBottomMenu={false}
              isReduced
              autofocus={false}
              placeholder="Saisir un commentaire sur la commande..."
              register={register}
              control={control}
              setValue={setValue}
              watch={watch}
              error={formState.errors.note}
            />
          </div>
        </>
      )}

      <div>
        <ToggleInput
          name="sendEmail"
          label={
            autoEmailEnabled
              ? "Modifier l'email de réponse automatique"
              : 'Envoyer un email'
          }
          register={register}
          setValue={setValue}
          watch={watch}
        />
        {emailHint && <p className={bulkEmailHint}>{emailHint}</p>}
        {recipientHint && <p className={bulkEmailHint}>{recipientHint}</p>}
      </div>
      {sendEmail && (
        <>
          <EmailTemplateSelector
            key={`template-${templateId ?? 'none'}`}
            name="templateId"
            subjectFieldName="emailSubject"
            contentFieldName="emailContent"
            register={register}
            setValue={setValue}
            watch={watch}
          />
          <TextInput
            name="emailSubject"
            label="Sujet"
            required
            register={register}
            control={control}
            setValue={setValue}
            watch={watch}
            error={formState.errors.emailSubject}
          />
          <EditorInput
            name="emailContent"
            rows={5}
            autofocus={false}
            required
            placeholderCategories={['Candidat', 'Commande', 'Offre', 'Agence']}
            register={register}
            control={control}
            setValue={setValue}
            watch={watch}
            error={formState.errors.emailContent}
          />
        </>
      )}
    </>
  )
}
